'use client';

import { useZipDownloadWorker } from '@/hooks/download/useZipDownloadWorker';
import { useProgressToast } from '@/hooks/progress/useProgressToast';
import { DOWNLOAD_FETCH_PROGRESS_KEY } from '@/lib/constants/job';
import {
  DOWNLOAD_CANCELLED_MESSAGE,
  type ZipDownloadProgressEvent,
} from '@/lib/download/zip-download-core';
import { DownloadSignedUrlsResult, SignedUrlEntry } from '@/types/download';
import { useCallback, useState } from 'react';

type UseDownloadFetchProgressOptions = {
  onCancel?: () => void;
};

export function useDownloadFetchProgress({ onCancel }: UseDownloadFetchProgressOptions = {}) {
  const [description, setDescription] = useState('Preparando descarga...');
  const [instant, setInstant] = useState(false);

  const { startControlled, setProgressValue, finish, cancel } = useProgressToast({
    label: 'Descargando imágenes',
    description,
    toastId: DOWNLOAD_FETCH_PROGRESS_KEY,
    instant,
    onCancel,
    cancelLabel: 'Cancelar descarga',
  });

  const { start: startZipDownload, cancel: cancelZipDownload } = useZipDownloadWorker({
    onInstantChange: setInstant,
  });

  const applyProgress = useCallback(
    (progress: ZipDownloadProgressEvent) => {
      if (progress.phase === 'fetching') {
        const processed = progress.downloaded + progress.failed;
        setDescription(`${processed} de ${progress.total} imágenes procesadas...`);
      } else {
        setDescription('Generando archivo ZIP...');
      }
      setProgressValue(progress.percent);
    },
    [setProgressValue],
  );

  const downloadWithProgress = useCallback(
    async (
      signedUrls: SignedUrlEntry[],
      zipName: string,
      saveHandle?: FileSystemFileHandle | null,
    ): Promise<DownloadSignedUrlsResult> => {
      if (signedUrls.length === 0) {
        return { downloaded: 0, failedCount: 0 };
      }

      startControlled();
      setDescription('0 imágenes descargadas...');
      setProgressValue(0);
      setInstant(false);

      try {
        const result = await startZipDownload(signedUrls, zipName, saveHandle, applyProgress);
        finish();
        return result;
      } catch (error) {
        if ((error as Error).message !== DOWNLOAD_CANCELLED_MESSAGE) {
          cancelZipDownload();
        }
        cancel();
        throw error;
      }
    },
    [
      startControlled,
      setProgressValue,
      finish,
      cancel,
      startZipDownload,
      cancelZipDownload,
      applyProgress,
    ],
  );

  const cancelDownload = useCallback(() => {
    cancelZipDownload();
    cancel();
  }, [cancelZipDownload, cancel]);

  return { downloadWithProgress, cancelDownload };
}
